import { Link } from "react-router-dom";
import { ArrowRight, QrCode, Wallet, Send, ShieldCheck, Zap, BadgeCheck, Building2, CheckCircle2 } from "lucide-react";
import { Brand } from "@/components/Brand";

const FEATURES = [
  { icon: QrCode, t: "Static QR Collections", d: "Get a dedicated static UPI QR and UPI ID. Customers scan with any UPI app and pay instantly." },
  { icon: Wallet, t: "3 Smart Wallets", d: "Collection, Payout and Chargeback wallets keep every rupee organised and auditable." },
  { icon: Send, t: "Bank Payouts", d: "Withdraw to any bank account with just holder name, account number and IFSC." },
  { icon: BadgeCheck, t: "12-digit UTR Tracking", d: "Every credit and payout is matched with a bank UTR reference for full reconciliation." },
  { icon: ShieldCheck, t: "Chargeback Reserve", d: "A dedicated reserve wallet handles disputes and adjustments transparently." },
  { icon: Zap, t: "Public Pay Link", d: "Share yespaybizz pay link - your customers see your QR and UPI ID in one tap." },
];

const Nav = () => (
  <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0A1F44]/85 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
      <Brand dark />
      <nav className="hidden items-center gap-8 text-sm text-slate-300 md:flex">
        <a href="#features" className="hover:text-white" data-testid="nav-features-link">Features</a>
        <a href="#how" className="hover:text-white" data-testid="nav-how-link">How it works</a>
        <a href="#wallets" className="hover:text-white" data-testid="nav-wallets-link">Wallets</a>
      </nav>
      <Link to="/login" data-testid="nav-login-btn" className="rounded-md bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
        Login
      </Link>
    </div>
  </header>
);

const QrMock = () => (
  <div className="rise d2 relative mx-auto w-full max-w-sm">
    <div className="absolute -inset-6 rounded-3xl bg-[#2563EB]/25 blur-3xl" />
    <div className="relative rounded-2xl border border-white/10 bg-white p-6 shadow-2xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Paying to</div>
          <div className="font-display text-lg font-bold text-[#0A1F44]">Your Business</div>
        </div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">UPI</span>
      </div>
      <div className="relative mx-auto mt-5 grid aspect-square w-full place-items-center overflow-hidden rounded-xl border-2 border-dashed border-slate-200 bg-slate-50">
        <div className="grid grid-cols-7 gap-1 p-6">
          {Array.from({ length: 49 }).map((_, i) => (
            <span key={i} className={`h-5 w-5 rounded-[3px] ${[0,1,2,7,9,14,15,16,4,5,6,11,13,18,19,20,28,29,30,35,37,42,43,44,24,26,32,40,46,22,10].includes(i) ? "bg-[#0A1F44]" : "bg-transparent"}`} />
          ))}
        </div>
        <div className="scan-line absolute left-4 right-4 h-0.5 bg-[#2563EB] shadow-[0_0_12px_#2563EB]" />
      </div>
      <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-center font-mono text-sm text-slate-600">merchant@upi</div>
      <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4" /> ₹2,499.00 credited · UTR 4127•••••903
      </div>
    </div>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0A1F44] grid-bg">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:py-28">
      <div>
        <span className="rise inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-sky-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Static QR Payment Gateway
        </span>
        <h1 className="rise d1 mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          Accept UPI payments.<br /><span className="text-[#5B9BFF]">Settle with confidence.</span>
        </h1>
        <p className="rise d2 mt-6 max-w-xl text-base leading-relaxed text-slate-300">
          YesPayBizz gives your business a dedicated static QR, a public pay link and three powerful wallets — with UTR-verified credits and bank payouts.
        </p>
        <div className="rise d3 mt-9 flex flex-wrap gap-3">
          <Link to="/login" data-testid="hero-get-started-btn" className="group inline-flex items-center gap-2 rounded-md bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
            Merchant Login <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a href="#features" data-testid="hero-explore-btn" className="rounded-md border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
            Explore features
          </a>
        </div>
        <div className="rise d4 mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8">
          {[["UPI", "All apps supported"], ["3", "Wallets"], ["12-digit", "UTR tracking"]].map(([a, b]) => (
            <div key={b}><div className="font-display text-2xl font-bold text-white">{a}</div><div className="mt-1 text-xs text-slate-400">{b}</div></div>
          ))}
        </div>
      </div>
      <QrMock />
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="bg-white py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider text-[#2563EB]">Gateway features</p>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-[#0A1F44] sm:text-4xl">Everything you need to collect and disburse</h2>
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f) => (
          <div key={f.t} className="group bg-white p-8 transition-colors hover:bg-slate-50">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-blue-50 text-[#2563EB] transition-transform group-hover:-translate-y-1"><f.icon className="h-5 w-5" /></span>
            <h3 className="mt-5 font-display text-lg font-semibold text-[#0A1F44]">{f.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const How = () => (
  <section id="how" className="bg-[#F4F7FB] py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <h2 className="font-display text-3xl font-bold tracking-tight text-[#0A1F44] sm:text-4xl">How it works</h2>
      <div className="mt-12 grid gap-6 md:grid-cols-4">
        {[["01", "Get onboarded", "Our team creates your merchant account and assigns your static QR and UPI ID."],
          ["02", "Collect payments", "Customers scan your QR or open your pay link and pay through any UPI app."],
          ["03", "Wallet credited", "Payments are verified with the 12-digit UTR and credited to your Collection wallet."],
          ["04", "Request payout", "Move funds to Payout wallet and withdraw directly to your bank account."]].map(([n, t, d]) => (
          <div key={n} className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="font-mono text-sm font-semibold text-[#2563EB]">{n}</div>
            <h3 className="mt-4 font-display text-lg font-semibold text-[#0A1F44]">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Wallets = () => (
  <section id="wallets" className="bg-[#0A1F44] py-24">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
      <div>
        <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Three wallets. Zero confusion.</h2>
        <p className="mt-4 max-w-lg text-slate-300">Separate balances for what you collect, what you can withdraw and what is held for chargebacks — always visible on your dashboard.</p>
        <Link to="/login" data-testid="wallets-login-btn" className="mt-8 inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-semibold text-[#0A1F44] transition-colors hover:bg-slate-100">
          Open Dashboard <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-4">
        {[["Collection Wallet", "Incoming QR payments", "bg-emerald-400"], ["Payout Wallet", "Ready for bank transfer", "bg-blue-400"], ["Chargeback Wallet", "Dispute reserve", "bg-amber-400"]].map(([t, d, c]) => (
          <div key={t} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur">
            <span className={`h-10 w-1.5 rounded-full ${c}`} />
            <div><div className="font-display font-semibold text-white">{t}</div><div className="text-sm text-slate-400">{d}</div></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white py-10">
    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-5 sm:flex-row sm:items-center sm:px-8">
      <Brand />
      <div className="flex items-center gap-2 text-sm text-slate-500"><Building2 className="h-4 w-4" /> © {new Date().getFullYear()} YesPayBizz. All rights reserved.</div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Nav /><Hero /><Features /><How /><Wallets /><Footer />
    </div>
  );
}
